/**
 * Internal, privacy-friendly audience tracking (no cookie, no personal data, no third party).
 * Events are queued and sent in small batches to POST /api/analytics/track.
 */
import { logError } from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${BACKEND_URL}/api/analytics/track`;
const VID_KEY = 'ps_vid';
const SID_KEY = 'ps_sid';
const ENTRY_KEY = 'ps_entry_sent';
const FLUSH_DELAY_MS = 1200;

let queue = [];
let timer = null;
let listenersBound = false;

const uid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

let storageWarned = false;
const warnStorage = (error) => {
  // Private mode / blocked storage: tracking simply stays session-less. Log once, never disturb the visitor.
  if (storageWarned) return;
  storageWarned = true;
  logError('analytics storage unavailable', error);
};
const safeGet = (storage, key) => {
  try { return storage.getItem(key); } catch (error) { warnStorage(error); return null; }
};
const safeSet = (storage, key, value) => {
  try { storage.setItem(key, value); } catch (error) { warnStorage(error); }
};

/** Anonymous visitor id (persists across visits on the same browser). */
export function getVisitorId() {
  if (typeof window === 'undefined') return null;
  let v = safeGet(window.localStorage, VID_KEY);
  if (!v) { v = uid(); safeSet(window.localStorage, VID_KEY, v); }
  return v;
}

/** Anonymous session id (one per browser tab session). */
export function getSessionId() {
  if (typeof window === 'undefined') return null;
  let s = safeGet(window.sessionStorage, SID_KEY);
  if (!s) { s = uid(); safeSet(window.sessionStorage, SID_KEY, s); }
  return s;
}

function send(useBeacon) {
  if (!queue.length) return;
  const body = JSON.stringify({ events: queue.splice(0, 25) });
  if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))) return;
    } catch (error) {
      logError('analytics beacon', error); // fall back to fetch below
    }
  }
  fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
    .catch((error) => logError('analytics send', error)); // audience tracking must never disturb the visitor
}

export function flush(useBeacon = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  send(useBeacon);
}

function schedule() {
  if (timer) return;
  timer = setTimeout(() => { timer = null; send(false); }, FLUSH_DELAY_MS);
}

function bindLifecycle() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(true); });
  window.addEventListener('pagehide', () => flush(true));
}

function push(event, immediate = false) {
  if (typeof window === 'undefined') return;
  const path = event.path || window.location.pathname;
  if (path.startsWith('/admin')) return;
  bindLifecycle();
  queue.push({ ...event, path, vid: getVisitorId(), sid: getSessionId() });
  if (immediate) flush(false); else schedule();
}

/** A page has been displayed (SPA navigation). */
export function trackPageView(path, title) {
  const entry = !safeGet(window.sessionStorage, ENTRY_KEY);
  if (entry) safeSet(window.sessionStorage, ENTRY_KEY, '1');
  push({ type: 'page_view', path, title: (title || '').slice(0, 200), referrer: entry ? (document.referrer || '') : '', entry });
}

/** The cost simulator has been used (service: 'menage' | 'jardinage'). */
export function trackSimulator(service, path) {
  push({ type: 'simulator', service, path });
}

/** A conversion trigger: 'phone' | 'whatsapp' | 'email' | 'contact' (form submissions are counted server-side). */
export function trackTrigger(name, path) {
  push({ type: 'trigger', name, path }, true);
}
